import React from 'react';
import { MedicineDetailResult, Wholesale } from 'features/graphql';
import { resolvePriceTypeLabel } from './priceTypeLabel';

interface PriceShowWholesaleCellsProps {
  medicine: MedicineDetailResult;
  /** 行の種類: 0=年度, 1=空白, 2=価格種別, 3=価格, 4=割引率 */
  rowIndex: number;
  cellBackgroundColor: string;
  wholesales: Wholesale[];
  /** 「安」マーク列を表示するか（全医薬品にわたる判定のため親で算出して渡す） */
  showMinPriceColumn: boolean;
  zoomLevel: number;
  previousYear: number;
  facilityStatus: string;
}

/** 価格種別ラベル（判定は resolvePriceTypeLabel に集約。表示は色付きspanで返す） */
const priceTypeLabel = (facilityStatus: string, priceType: string | null | undefined) =>
  resolvePriceTypeLabel(facilityStatus, priceType) === '参考価格' ? (
    <span>参考価格</span>
  ) : (
    <span className="text-success">妥結価</span>
  );

/** 1セルの表示内容を行種別(rowIndex)に応じて返す（switchをmapコールバックから分離） */
const renderCellContent = (
  rowIndex: number,
  hasDeal: boolean,
  wholesalePrice: number | null | undefined,
  discountRate: number | string | null | undefined,
  priceType: string | null | undefined,
  hasPurchaseHistoryForYear: boolean | null | undefined,
  previousYear: number,
  facilityStatus: string
): React.ReactNode => {
  switch (rowIndex) {
    case 0:
      return hasDeal ? `${previousYear}年度` : '-';

    case 1:
      return ' ';

    case 2:
      return hasDeal ? priceTypeLabel(facilityStatus, priceType) : <span>-</span>;

    case 3: // 価格表示（「安」マークは別列に移動）
      return hasDeal && wholesalePrice ? (
        <span
          style={{
            backgroundColor: hasPurchaseHistoryForYear ? '#ffaa55' : 'transparent',
            padding: hasPurchaseHistoryForYear ? '2px 4px' : '0',
            borderRadius: hasPurchaseHistoryForYear ? '3px' : '0',
          }}
        >
          {Math.floor(wholesalePrice).toLocaleString()}
        </span>
      ) : (
        <span>-</span>
      );

    case 4: // 割引率
      return hasDeal && discountRate != null ? (
        <span>
          {typeof discountRate === 'number' ? discountRate.toFixed(2) : discountRate.toString()}%
        </span>
      ) : (
        <span>-</span>
      );

    default:
      return null;
  }
};

/**
 * 価格表の卸ごとのセル群を描画する（D4 step3: PriceShowList から renderWholesaleCells を分離）。
 * 1医薬品・1行(rowIndex)分の「安」マーク列＋価格列を卸の数だけ返す。挙動は分離前と不変。
 */
export const PriceShowWholesaleCells: React.FC<PriceShowWholesaleCellsProps> = ({
  medicine,
  rowIndex,
  cellBackgroundColor,
  wholesales,
  showMinPriceColumn,
  zoomLevel,
  previousYear,
  facilityStatus,
}) => {
  if (!wholesales || wholesales.length === 0) {
    return null;
  }

  // 最安値計算（価格表示行でのみ必要）
  let minPrice: number | null = null;
  const wholesalePrices: Record<string, number | null> = {};

  if (rowIndex === 3) {
    // 4行目（価格表示行）の場合のみ最小価格を計算
    wholesales.forEach((wholesale) => {
      const deal = medicine.deals?.find((d) => d?.wholesaleId === wholesale.wholesaleId);
      const price = deal?.packagingPrice;
      wholesalePrices[wholesale.wholesaleId] = price ?? null;
      if (price != null && (minPrice === null || price < minPrice)) {
        minPrice = price;
      }
    });
  }

  return (
    <>
      {wholesales.map((wholesale) => {
        const deal = medicine.deals?.find((d) => d?.wholesaleId === wholesale.wholesaleId);
        const wholesalePrice = deal?.packagingPrice;
        const discountRate = deal?.discountRate;
        const priceType = deal?.priceType;
        const hasPurchaseHistoryForYear = deal?.hasPurchaseHistoryForYear;

        // 最安値判定用の価格を取得
        const price = wholesalePrices[wholesale.wholesaleId];
        const isMinPrice =
          rowIndex === 3 && price !== null && minPrice !== null && price === minPrice;

        return (
          <React.Fragment key={wholesale.wholesaleId}>
            {/* 「安」マーク専用列 - 条件付きで表示 */}
            {showMinPriceColumn && (
              <td
                style={{
                  backgroundColor: cellBackgroundColor,
                  width: `${1.5 * zoomLevel}rem`,
                  minWidth: `${1.5 * zoomLevel}rem`,
                  maxWidth: `${1.5 * zoomLevel}rem`,
                  textAlign: 'center',
                  padding: '0.1rem',
                }}
              >
                {rowIndex === 3 && isMinPrice && (
                  <span style={{ color: 'red', fontWeight: 'bold' }}>安</span>
                )}
              </td>
            )}

            {/* 価格表示列 */}
            <td style={{ backgroundColor: cellBackgroundColor, padding: '0.2rem 0.3rem' }}>
              {renderCellContent(
                rowIndex,
                deal != null,
                wholesalePrice,
                discountRate,
                priceType,
                hasPurchaseHistoryForYear,
                previousYear,
                facilityStatus
              )}
            </td>
          </React.Fragment>
        );
      })}
    </>
  );
};
